// ==============================================
// AdminMenuNav — the single admin menu, rendered from the saved Menu Builder
// config reconciled against ADMIN_MENU_CATALOG. Used by both the Admin Panel
// sidebar (AdminLayout) and the dashboard's ADMIN section (DashboardLayout),
// so the two can never drift apart. Only the accent colour differs.
// ==============================================

import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../providers/AuthProvider';
import { api } from '../../services/api';
import {
  ADMIN_MENU_BY_ID,
  reconcileMenuConfig,
  type MenuVisibility,
} from '../../config/adminMenuCatalog';

export type AdminMenuAccent = 'red' | 'purple';

const activeClasses: Record<AdminMenuAccent, string> = {
  red: 'bg-red-500/20 text-red-400',
  purple: 'bg-purple-500/10 text-purple-400',
};

function visibleTo(visibility: MenuVisibility, isSuper: boolean): boolean {
  if (visibility === 'hidden') return false;
  if (visibility === 'super') return isSuper;
  return true; // 'admin' → visible to all admins
}

export interface AdminMenuNavProps {
  accent: AdminMenuAccent;
  /** Called after a link is clicked (e.g. to close the mobile sidebar). */
  onNavigate?: () => void;
}

export function AdminMenuNav({ accent, onNavigate }: AdminMenuNavProps) {
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';

  const { data } = useQuery({
    queryKey: ['admin', 'menu-config'],
    queryFn: () => api.getAdminMenuConfig(),
    staleTime: 5 * 60 * 1000,
  });
  const config = reconcileMenuConfig(data);

  // Groups always start collapsed on load; the admin expands the ones they want.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggle = (id: string) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
      isActive ? activeClasses[accent] : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
    }`;

  const renderItem = (id: string) => {
    const item = ADMIN_MENU_BY_ID[id];
    if (!item) return null;
    return (
      <li key={id}>
        <NavLink to={item.href} end={item.end} onClick={onNavigate} className={navLinkClass}>
          <item.icon className="w-5 h-5" />
          <span>{item.label}</span>
        </NavLink>
      </li>
    );
  };

  return (
    <ul className="space-y-1">
      {config.nodes.map((node) => {
        if (node.type === 'item') {
          return visibleTo(node.visibility, isSuper) ? renderItem(node.id) : null;
        }
        const visibleItems = node.items.filter((it) => visibleTo(it.visibility, isSuper));
        if (visibleItems.length === 0) return null;
        const open = !!expanded[node.id];
        return (
          <li key={node.id} className="pt-2">
            <button
              type="button"
              onClick={() => toggle(node.id)}
              className="w-full flex items-center justify-between px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-300"
            >
              <span>{node.title}</span>
              {open ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
            </button>
            {open && <ul className="space-y-1 mt-1">{visibleItems.map((it) => renderItem(it.id))}</ul>}
          </li>
        );
      })}
    </ul>
  );
}

export default AdminMenuNav;
